// Reusable accessibility plumbing for modal dialogs.
//
// Bare fixed-position <div> modals give screen reader and keyboard users
// nothing: no role, no focus move, no Escape, no way back. This hook wires
// the four essentials onto any dialog container:
//   1. move focus into the dialog on open (container or a supplied first field)
//   2. restore focus to the element that opened it on close (unmount)
//   3. close on Escape
//   4. a simple focus trap so Tab wraps within the dialog
//
// It returns a ref to attach to the dialog element (the node that carries
// role="dialog"). Attach it, give that node tabIndex={-1} so it can receive
// programmatic focus, and you are done.
//
// Because our map modals mount when opened and unmount when closed, the
// mount/unmount lifecycle IS the open/close lifecycle, so the focus store and
// restore live in a mount-only effect. onClose is read through a ref so the
// Escape listener never has to be town down and rebuilt on every parent render.

import { useEffect, useRef } from 'react';

/**
 * True for the Escape key across the older ('Esc') and modern ('Escape') names.
 * @param {string} key
 * @returns {boolean}
 */
export function isEscapeKey(key) {
  return key === 'Escape' || key === 'Esc';
}

/**
 * Focus an element if it is a real, still-attached, focusable node.
 * Returns whether focus was actually moved so callers/tests can assert on it.
 * @param {Element | null | undefined} el
 * @returns {boolean}
 */
export function restoreFocus(el) {
  if (
    el &&
    typeof el.focus === 'function' &&
    typeof document !== 'undefined' &&
    document.contains(el)
  ) {
    el.focus();
    return true;
  }
  return false;
}

// Elements that can hold keyboard focus inside a dialog. [tabindex="-1"] is
// deliberately excluded (programmatic-only), as are disabled controls.
const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'textarea:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

/**
 * The visible, focusable descendants of a container, in DOM order.
 * @param {Element | null} container
 * @returns {HTMLElement[]}
 */
export function getFocusable(container) {
  if (!container || typeof container.querySelectorAll !== 'function') return [];
  return Array.from(container.querySelectorAll(FOCUSABLE_SELECTOR)).filter(
    (el) => el.getClientRects().length > 0 || el === document.activeElement,
  );
}

/**
 * @param {object} [opts]
 * @param {() => void} [opts.onClose] - called on Escape.
 * @param {{ current: HTMLElement | null }} [opts.initialFocusRef] - focus this on open instead of the container.
 * @param {boolean} [opts.trapFocus=true] - keep Tab focus inside the dialog.
 * @returns {{ current: HTMLElement | null }} ref for the dialog element (role="dialog").
 */
export function useDialogA11y({ onClose, initialFocusRef, trapFocus = true } = {}) {
  const dialogRef = useRef(null);
  const previouslyFocusedRef = useRef(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    // Remember who had focus so we can hand it back when the dialog closes.
    previouslyFocusedRef.current =
      typeof document !== 'undefined' ? document.activeElement : null;

    // Move focus into the dialog: a named first field if given, else the
    // container itself (which the caller makes focusable with tabIndex={-1}).
    const focusTarget =
      (initialFocusRef && initialFocusRef.current) || dialogRef.current;
    if (focusTarget && typeof focusTarget.focus === 'function') {
      focusTarget.focus();
    }

    const handleKeyDown = (e) => {
      const dialog = dialogRef.current;
      // Only the dialog that actually holds focus responds. This scopes Escape
      // and the trap to the topmost dialog when one is stacked over another
      // (our ContactModal opens on top of JobCardModal).
      if (!dialog || !dialog.contains(document.activeElement)) return;

      if (isEscapeKey(e.key)) {
        if (onCloseRef.current) onCloseRef.current();
        return;
      }

      if (trapFocus && e.key === 'Tab') {
        const focusables = getFocusable(dialog);
        if (focusables.length === 0) {
          // Nothing tabbable inside: keep focus pinned to the dialog shell.
          e.preventDefault();
          dialog.focus();
          return;
        }
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        const active = document.activeElement;
        if (e.shiftKey && (active === first || active === dialog)) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && active === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      restoreFocus(previouslyFocusedRef.current);
    };
    // Mount-only: mount === open, unmount === close for these modals.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return dialogRef;
}
